import {
  getGetFrontRichiesteIdModuliIdQueryKey,
  getGetFrontRichiesteIdModuliQueryKey,
  getGetFrontRichiesteIdQueryKey,
} from '@framework/api-client';
import { TipoDomandaDTO } from '@framework/api-client/src/api/backoffice/schemas';
import { useCurrentUser } from '@framework/auth';
import {
  ISubmissionNavigator,
  ModuleForm,
  ModuleStateMachine,
  useDelegationContext,
  useModule,
} from '@framework/frontoffice-submission';
import { t } from '@framework/i18n';
import { useIdFromUrl } from '@framework/ui';
import { message, Typography } from 'antd';
import { useMemo } from 'react';
import { RouteComponentProps } from 'react-router';
import { queryClient } from '../../../../core/network/queryClient';
import { NetworkAlertPage } from '../../../../routes/errors/network-alert/NetworkAlertPage';
import { LoadingPage } from '../../../../routes/loading/LoadingPage';
import { useModuleSaverPrehooks } from '../../hooks/useModuleSaverPrehooks';
import { ModuleStateBottomBox } from './ModuleStateBottomBox';
import { ModuleStateTopBox } from './ModuleStateTopBox';

const { Text } = Typography;

interface Params {
  id: string;
  moduleId: string;
}

export interface ICompilationModuleCardProps
  extends RouteComponentProps<Params> {
  handleNavigation: ISubmissionNavigator;

  /**
   * Id del modulo iniziale da visualizzare.
   */
  initialModuleId?: string | null;

  /**
   * Forzo la visualizzazione in readonly
   */
  // TODO Il check su `true` non sembra funzionare, accetta comunque un boolean
  readonly?: true;

  inquiry?: TipoDomandaDTO;
}

async function onSuccess(submissionId: string | null, moduleId: string | null) {
  if (submissionId && moduleId) {
    await queryClient.invalidateQueries(getGetFrontRichiesteIdQueryKey(submissionId)); // prettier-ignore
    await queryClient.invalidateQueries(getGetFrontRichiesteIdModuliIdQueryKey(submissionId, moduleId)); // prettier-ignore
    await queryClient.invalidateQueries(getGetFrontRichiesteIdModuliQueryKey(submissionId)); // prettier-ignore
  }
  message.success(
    t(
      'compilation-module.modulo-salvato-con-successo',
      'Modulo salvato con successo',
    ),
  );
}

/**
 * Frame di compilazione di un modulo inserito all'interno della `SubmissionPage`.
 */
export function CompilationModuleCard(props: ICompilationModuleCardProps) {
  const { handleNavigation, initialModuleId, readonly, inquiry } = props;
  const [submissionId] = useIdFromUrl(props, 'id');
  const [submissionModuleId] = useIdFromUrl(props, 'moduleId', initialModuleId);

  const user = useCurrentUser();

  const { module, submission, dossier, isLoading, isFetching, error } =
    useModule({
      submissionId,
      submissionModuleId,
    });

  const delegationContext = useDelegationContext();

  const moduleMachineRecord = useMemo(() => {
    return {
      module: module,
      submission: submission,
      delegationData: delegationContext?.delegationData,
    };
  }, [module, submission, delegationContext?.delegationData]);

  const { onAfterSave, onBeforeSave } = useModuleSaverPrehooks({
    record: moduleMachineRecord,
    user: user,
  });

  if (isLoading) return <LoadingPage noLayout />;

  if (error) {
    return (
      <NetworkAlertPage
        noLayout
        message={t(
          'compilation-module.impossibile-caricare-i-moduli',
          'Impossibile caricare i Moduli della Domanda',
        )}
        error={error}
      />
    );
  }

  if (!module || !submission) {
    return (
      <NetworkAlertPage
        noLayout
        message={t(
          'compilation-module.modulo-non-trovato',
          'Modulo non trovato',
        )}
        error={error}
      />
    );
  }

  return (
    <ModuleForm
      module={module}
      submission={submission}
      inquiry={inquiry}
      dossier={dossier}
      submissionId={submissionId}
      isFetching={isFetching}
      handleNavigation={handleNavigation}
      onDraftSuccess={async () => {
        await onSuccess(submissionId, submissionModuleId);
      }}
      onSaveSuccess={async () => {
        await onSuccess(submissionId, submissionModuleId);
      }}
      readonly={readonly}
      before={<ModuleStateTopBox data={moduleMachineRecord} />}
      after={<ModuleStateBottomBox data={moduleMachineRecord} />}
      onAfterSave={onAfterSave}
      onBeforeSave={onBeforeSave}
      canAutoComplete={
        ModuleStateMachine.can(moduleMachineRecord, '.autoComplete', user)
          .allowed
      }
    />
  );
}
